import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { EnhancedDataGrid } from 'components/enhanced/data-grid';
import DataGridCell from 'components/enhanced/data-grid/components/cell/DataGridCell';
import {
  filterOperators,
  numberFilterOperators
} from 'components/enhanced/data-grid/utils/filters';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { required } from 'components/helpers/validators';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import AddEnhancedButton from 'components/ui/buttons/AddEnhancedButton';
import TenantPage from 'layouts/TenantPage';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productService } from 'shared/services';
import { loadingActions } from 'store';
import { useAppDispatch } from 'store/store';
import { Option } from 'shared/types/general';
import { skuMappingService } from './sku-mapping.service';
import { baseCell } from 'components/cells';
import { EnhancedDialog } from 'components/enhanced';

export const ProductsSkuMapping = () => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.sku';
  const dispatch = useAppDispatch();
  const datagridRefresh = useRef(null);
  const isDisableEdit = useIsTechnician();

  const columns: GridColDef[] = [
    {
      field: 'sku',
      headerName: t(`${translationPrefix}.entity.sku`),
      filterOperators,
      width: 200,
      editable: !isDisableEdit,
      renderCell: (params: GridRenderCellParams<any>) => (
        <DataGridCell content={params.value} sx={{ fontWeight: 'bold' }} />
      )
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      filterOperators,
      width: 300,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      filterOperators,
      flex: 1,
      renderCell: baseCell
    },
    {
      field: 'gtin_product_batch_max_chars',
      headerName: t(`entities.contract.gtin_product_batch_max_chars`),
      filterOperators: numberFilterOperators,
      editable: !isDisableEdit,
      flex: 1,
      renderCell: baseCell
    }
  ];

  const [openDialog, setOpenDialog] = useState(false);
  const [products, setProducts] = useState<Option[]>([]);

  useEffect(() => {
    dispatch(loadingActions.startLoading());
    productService.getAllBaseInformation().then((res) => {
      setProducts(res);
      dispatch(loadingActions.stopLoading());
    });
  }, []);

  const onSubmitCreate = async (values) => {
    if (values && typeof values.preventDefault === 'function') {
      return;
    }
    dispatch(loadingActions.startLoading());
    try {
      await skuMappingService.add(values);
      setOpenDialog(false);
      if (datagridRefresh.current) {
        datagridRefresh.current();
      }
    } catch (error) {
      console.error('Failed to add SKU mapping:', error);
    } finally {
      dispatch(loadingActions.stopLoading());
    }
  };

  const onOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  return (
    <TenantPage
      title={t(`${translationPrefix}.title`)}
      subtitle={t(`${translationPrefix}.subtitle`)}
      menuRight={
        <AddEnhancedButton prefix={translationPrefix} onClick={onOpenDialog} requireTenantAdmin />
      }>
      <EnhancedDataGrid
        service={skuMappingService}
        columns={columns}
        refresh={datagridRefresh}
        disableExport={true}
      />
      <EnhancedDialog
        open={openDialog}
        onSubmit={onSubmitCreate}
        onClose={() => {
          setOpenDialog(false);
        }}
        title={t(`${translationPrefix}.add`)}
        submitButtonProps={{
          type: 'submit',
          form: 'addSkuMapping'
        }}>
        <EnhancedForm onSubmit={onSubmitCreate} formId="addSkuMapping" hideControls autoStyleLayout>
          <IdentityField
            name="sku"
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.entity.sku`)}
            type="text"
            validate={required}
            required
          />
          <IdentityField
            name="product"
            optionLabel="description"
            component={AutocompleteAdapter}
            options={products}
            label={t(`global.product`)}
            type="text"
            validate={required}
            required
          />
        </EnhancedForm>
      </EnhancedDialog>
    </TenantPage>
  );
};

export default ProductsSkuMapping;
